import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { AppointmentCard } from '../AppointmentCard';
import {
  getDateGroupLabel,
  groupAppointmentsByDate,
} from '../AppointmentCard/AppointmentCard.utils';

import type { AppointmentsListProps } from './AppointmentsList.types';

/**
 * Reusable component for displaying a list of appointments grouped by date
 * Manages its own expanded state internally
 */
export const AppointmentsList: React.FC<AppointmentsListProps> = ({
  appointments,
  onAppointmentClick,
  onReschedule,
  onCancel,
  onRecordSession,
  onViewSession,
  onViewProfile,
  showDateHeaders = true,
  defaultExpanded = 'none',
  analyticsSource,
}) => {
  const { appointment: appointmentTracking } = useEventTracking();
  // Determine initial expanded card based on defaultExpanded prop
  const getInitialExpanded = useCallback((): string | null => {
    if (!appointments.length || defaultExpanded === 'none') return null;

    if (defaultExpanded === 'first') {
      return appointments[0].appointment_id;
    }

    return null;
  }, [appointments, defaultExpanded]);

  // Internal expanded state with smart initialization
  const [expandedCard, setExpandedCard] = useState<string | null>(() => getInitialExpanded());

  // Track if user has manually interacted with accordions
  const hasUserInteracted = useRef(false);

  // Track if we've already logged the list view event
  const hasLoggedListView = useRef(false);

  // Track list view event on mount and when appointments change
  useEffect(() => {
    if (analyticsSource && appointments.length > 0 && !hasLoggedListView.current) {
      // Get unique dates
      const grouped = groupAppointmentsByDate(appointments);
      const dateKeys = Array.from(grouped.keys());

      appointmentTracking.listViewed({
        source: analyticsSource,
        appointment_count: appointments.length,
        // Include the date keys (formatted as YYYY-MM-DD)
        visible_dates: dateKeys,
      });

      hasLoggedListView.current = true;
    }
  }, [appointments, analyticsSource, appointmentTracking]);

  // Handle auto-expansion logic
  useEffect(() => {
    // If user has interacted, only update if their selection is no longer available
    if (hasUserInteracted.current) {
      const expandedStillExists =
        expandedCard && appointments.some(a => a.appointment_id === expandedCard);
      if (expandedCard && !expandedStillExists) {
        // User's selection was removed, reset interaction flag and apply default
        hasUserInteracted.current = false;
        setExpandedCard(getInitialExpanded());
      }
    } else {
      // No user interaction yet, apply default expansion logic
      if (defaultExpanded === 'first' && appointments.length > 0) {
        const firstId = appointments[0].appointment_id;
        if (expandedCard !== firstId) {
          setExpandedCard(firstId);

          // Track auto-expansion
          if (analyticsSource) {
            appointmentTracking.cardExpanded({
              appointment_id: firstId,
              source: analyticsSource,
              is_auto_expanded: true,
              position: 0,
            });
          }
        }
      }
    }
  }, [appointments, defaultExpanded, expandedCard, getInitialExpanded, analyticsSource, appointmentTracking]);

  // Return null for empty state (no message)
  if (!appointments.length) {
    return null;
  }

  // Render appointment card
  const renderAppointmentCard = (appointment: (typeof appointments)[0], index: number) => {
    return (
      <AppointmentCard
        key={appointment.appointment_id}
        appointment={appointment}
        expanded={expandedCard === appointment.appointment_id}
        onChange={(_, isExpanded) => {
          // Mark that user has interacted
          hasUserInteracted.current = true;
          const newExpanded = isExpanded ? appointment.appointment_id : null;
          setExpandedCard(newExpanded);
        }}
        analytics={analyticsSource ? { source: analyticsSource, position: index } : undefined}
        {...(onRecordSession && {
          onRecordSession: () => onRecordSession(appointment),
        })}
        {...(onViewSession && {
          onViewSession: () => onViewSession(appointment),
        })}
        {...(onViewProfile && {
          onViewProfile: () => onViewProfile(appointment),
        })}
        {...((onReschedule || onAppointmentClick) && {
          onReschedule: () => {
            if (onReschedule) {
              onReschedule(appointment);
            } else if (onAppointmentClick) {
              onAppointmentClick(appointment);
            }
          },
        })}
        {...(onCancel && {
          onCancel: () => {
            if (appointment.appointment_id) {
              onCancel(appointment.appointment_id, appointment.series?.series_id);
            }
          },
        })}
      />
    );
  };

  // If not showing date headers, just render appointments
  if (!showDateHeaders) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {appointments.map((appointment, index) => renderAppointmentCard(appointment, index))}
      </Box>
    );
  }

  // Group appointments by date and render with headers
  const grouped = groupAppointmentsByDate(appointments);
  const sortedDates = Array.from(grouped.keys()).sort();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {sortedDates.map(dateKey => {
        const dateAppointments = grouped.get(dateKey) || [];
        if (dateAppointments.length === 0) return null;

        const dateLabel = getDateGroupLabel(dateAppointments[0].start_time);
        const isRelativeDate = ['YESTERDAY', 'TODAY', 'TOMORROW'].includes(dateLabel);

        return (
          <Box key={dateKey}>
            {/* Date Group Heading */}
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                mb: 2,
                mt: 0,
                fontSize: isRelativeDate ? '0.875rem' : '0.9375rem',
                letterSpacing: isRelativeDate ? '0.1em' : 'normal',
                textTransform: isRelativeDate ? 'uppercase' : 'none',
              }}
            >
              {dateLabel}
            </Typography>

            {/* Appointments for this date */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {dateAppointments.map((appointment, index) => renderAppointmentCard(appointment, index))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
